import {useEffect, useState} from 'react'
import Drawer from 'react-modern-drawer'
import {useAuth} from '../../../modules/auth'
import {
  addDoc,
  arrayRemove,
  collection,
  doc,
  getDoc,
  onSnapshot,
  runTransaction,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore'
import db from '../../../../services/firebase'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import AddRiskControls from './modal/AddRiskControls'
import {useNavigate} from 'react-router-dom'
import UpgradePlan from 'app/components/UpgradePlan'
import {showErrorToast, showSuccessToast} from 'app/components/ShowToast'
import ConfirmationModal from 'app/components/ConfirmationModal'
import useAuditContext from 'app/Hooks/useAuditContext'
import Button from 'app/components/Button'
import RiskSlider from 'app/components/RiskSlider'

type Props = {
  id: string
  isOpen: boolean
  onClose: () => void
}

type Control = {
  id: string
  control_title: string
  control_framework_mappings: string
}

const EditRiskModal: React.FC<Props> = ({id, isOpen, onClose}) => {
  const [riskSource, setRiskSource] = useState('')
  const [owner, setRiskOwner] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [loading, setLoading] = useState(false)
  const [owners, setOwners] = useState<any[]>([])
  const {currentUser} = useAuth()
  const [inherent_likelihood, setInherentLikelihood] = useState('')
  const [inherent_impact, setInherentImpact] = useState('')
  const [residual_likelihood, setResidualLikelihood] = useState('')
  const [residual_impact, setResidualImpact] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState<string[]>([])
  const [treatment, setTreatment] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [risk_status, setRiskStatus] = useState('')
  const [addedControls, setAddedControls] = useState<Control[]>([])
  const navigate = useNavigate()
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [showModal2, setShowModal2] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [toDeleteControl, setToDeleteControl] = useState<any>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const {currentAuditContext} = useAuditContext()

  const findOwnerUid = (ownerName: string) => {
    const selectedOwner = owners.find((owner) => owner.full_name === ownerName)
    return selectedOwner?.uid || selectedOwner?.id || ''
  }

  useEffect(() => {
    const tenantId = currentUser?.tenantId || ''
    if (!tenantId) {
      return
    }
    const tenantRef = doc(db, 'tenants', tenantId)
    const PolicyRef = collection(tenantRef, 'employees')

    const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
      const risksData = snapshot.docs
        .map((doc) => ({
          ...doc.data(),
          id: doc.id,
          end_date: doc.data().end_date,
        }))
        .filter((data) => !data.end_date)
      setOwners(risksData)
    })

    return unsubscribe
  }, [])

  const handleCloseModal = () => {
    setShowModal(false)
  }

  const validateForm = () => {
    const errors: Record<string, string> = {}

    if (!riskSource.trim()) {
      errors.riskSource = 'Risk name is required'
    }

    if (!description.trim()) {
      errors.description = 'Risk description is required'
    }

    if (!category) {
      errors.category = 'Risk category is required'
    }
    if (!owner) {
      errors.owner = 'Risk Owner is required'
    }

    if (!treatment) {
      errors.treatment = 'Treatment plan is required'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e) => {
    setIsLoading(true)
    e.preventDefault()

    if (!validateForm()) {
      setIsLoading(false)
      return
    }

    const ownerUid = findOwnerUid(owner)

    const newRisk = {
      riskSource,
      description,
      category,
      owner,
      owner_uid: ownerUid,
      inherent_impact,
      inherent_likelihood,
      residual_impact,
      residual_likelihood,
      treatment,
    }

    const modalContentElement = document.getElementById('kt_modal_9')

    const currentDate = new Date()
    const year = currentDate.getFullYear()
    const month = currentDate.getMonth() + 1
    const day = currentDate.getDate()

    const todaysDate = month + '/' + day + '/' + year

    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)

      const assetRef = doc(tenantRef, 'risks', id)
      await updateDoc(assetRef, {
        ...newRisk,
        risk_status: 'Needs approval',
        updated_at: serverTimestamp(),
      })

      const activitiesRef = collection(tenantRef, 'activities')
      await addDoc(activitiesRef, {
        message: `${currentUser?.userName} updated risk '${riskSource}'`,
        timestamp: serverTimestamp(),
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
        action: 'Risk scenario updated',
        target: 'Risk Management',
        username: currentUser?.userName,
        date: todaysDate,
        email: currentUser?.email,
      })
      showSuccessToast({message: 'Risk has been requested for approval'})

      if (modalContentElement) {
        modalContentElement.scrollTo({top: 0, left: 0, behavior: 'smooth'})
      }

      setIsLoading(false)
    } catch (err: any) {
      if (modalContentElement) {
        modalContentElement.scrollTo({top: 0, left: 0, behavior: 'smooth'})
      }
      showErrorToast({message: 'Failed to request for approval. Try again'})
      setIsLoading(false)
    }
  }

  const handleboard = () => {
    setShowModal(true)
  }

  useEffect(() => {
    if (id && isOpen) {
      const fetchInitialRiskData = async () => {
        setLoading(true)
        const tenantId = currentUser?.tenantId || ''
        const tenantRef = doc(db, 'tenants', tenantId)
        const riskDocRef = doc(collection(tenantRef, 'risks'), id)

        const docSnap = await getDoc(riskDocRef)
        if (docSnap.exists()) {
          const riskData = docSnap.data()
          setRiskSource(riskData.riskSource || '')
          setDescription(riskData.description || '')
          setCategory(riskData.category || [])
          setRiskOwner(riskData.owner || '')
          setInherentLikelihood(riskData.inherent_likelihood || '')
          setInherentImpact(riskData.inherent_impact || '')
          setResidualLikelihood(riskData.residual_likelihood || '')
          setResidualImpact(riskData.residual_impact || '')
          setTreatment(riskData.treatment || '')
          setRiskStatus(riskData.risk_status || '')
        }
        setLoading(false)
      }

      fetchInitialRiskData()
    }
  }, [id, isOpen])

  useEffect(() => {
    if (!id || !isOpen) return

    const tenantId = currentUser?.tenantId || ''
    const tenantRef = doc(db, 'tenants', tenantId)
    const riskDocRef = doc(collection(tenantRef, 'risks'), id)

    const unsubscribe = onSnapshot(riskDocRef, (doc) => {
      if (doc.exists()) {
        setAddedControls(doc.data().added_controls || [])
      }
    })

    return unsubscribe
  }, [id, isOpen])

  const handleChange = (event) => {
    setTreatment(event.target.value)
  }

  const updatePolicy = async (updateData) => {
    if (!currentUser?.tenantId || !id) return

    const tenantRef = doc(db, 'tenants', currentUser.tenantId)
    const policyRef = doc(collection(tenantRef, 'risks'), id)

    try {
      await runTransaction(db, async (transaction) => {
        const policyDoc = await transaction.get(policyRef)
        if (!policyDoc.exists()) {
          throw 'Document does not exist!'
        }

        transaction.update(policyRef, {
          ...updateData,
          updated_at: serverTimestamp(),
        })
      })
    } catch (error) {
      console.error(error)
    }
  }

  const handleApprove = async () => {
    try {
      setLoading(true)
      const newStatus = 'Approved'
      await updatePolicy({risk_status: newStatus})

      const currentDate = new Date()
      const year = currentDate.getFullYear()
      const month = currentDate.getMonth() + 1
      const day = currentDate.getDate()
      const todaysDate = `${month}/${day}/${year}`

      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const riskRef = doc(tenantRef, 'risks', id)

      await updateDoc(riskRef, {risk_status: newStatus})

      if (addedControls && addedControls.length > 0) {
        const updatePromises = addedControls.map(async (control) => {
          const controlRef = doc(tenantRef, 'controls', control.id)
          const controlDoc = await getDoc(controlRef)

          if (controlDoc.exists()) {
            const controlData = controlDoc.data()
            const updatedRiskScenario = controlData.risk_scenario.map((scenario: any) => {
              if (scenario.riskId === id) {
                return {...scenario, status: newStatus}
              }
              return scenario
            })

            await updateDoc(controlRef, {risk_scenario: updatedRiskScenario})
          }
        })

        await Promise.all(updatePromises)
      }

      const activitiesRef = collection(tenantRef, 'activities')
      await addDoc(activitiesRef, {
        message: `${currentUser?.userName} has approved risk scenario '${riskSource}'`,
        timestamp: serverTimestamp(),
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
        action: 'Risk scenario updated',
        target: 'Risk Management',
        username: currentUser?.userName,
        date: todaysDate,
        email: currentUser?.email,
      })
      showSuccessToast({message: 'Risk scenario has been approved'})
      setRiskStatus(newStatus)
    } catch (error) {
      showErrorToast({message: 'Failed to approve risk scenario. Try again'})
    }
    setLoading(false)
  }

  const handleDeleteControl = (control) => {
    setToDeleteControl(control)
    setIsDeleteModalOpen(true)
  }

  const handleConfirmControlDelete = async () => {
    try {
      setIsDeleting(true)
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const riskRef = doc(tenantRef, 'risks', id)
      const controlRef = doc(tenantRef, 'controls', toDeleteControl.id)

      const controlToDelete = addedControls.find((controls) => controls.id === toDeleteControl.id)

      if (!controlToDelete) {
        return
      }

      await updateDoc(riskRef, {
        added_controls: arrayRemove(controlToDelete),
      })

      const controlDoc = await getDoc(controlRef)
      if (controlDoc.exists()) {
        const controlData = controlDoc.data()
        const updatedRiskScenarios = controlData.risk_scenario.filter(
          (scenario) => scenario.riskId !== id
        )

        await updateDoc(controlRef, {
          risk_scenario: updatedRiskScenarios,
        })
      }

      setAddedControls((prevControls) =>
        prevControls.filter((controls) => controls.id !== toDeleteControl.id)
      )

      showSuccessToast({message: `${toDeleteControl.control_title} has been removed successfully.`})
    } catch (error) {
      showErrorToast({message: 'An error occurred while removing the control.'})
    } finally {
      setIsDeleting(false)
      setIsDeleteModalOpen(false)
      setToDeleteControl(null)
    }
  }

  const handleRiskClick = (id) => {
    navigate(`/controls?id=${id}`)
  }

  const onCancel = () => {
    setFormErrors({})
    setLoading(false)
    setRiskSource('')
    setRiskOwner('')
    setDescription('')
    setCategory([])
    setInherentLikelihood('')
    setInherentImpact('')
    setResidualLikelihood('')
    setResidualImpact('')
    setTreatment('')
    setRiskStatus('')
    setAddedControls([])
    onClose()
  }

  const getBadgeClass = (risk) => {
    if (risk < 5) {
      return {backgroundColor: '#0E9F6E'}
    } else if (risk >= 5 && risk <= 14) {
      return {backgroundColor: '#ffc107'}
    } else if (risk >= 15 && risk < 25) {
      return {backgroundColor: '#dc3545'}
    } else if (risk >= 25) {
      return {backgroundColor: '#A7222F'}
    } else {
      return {backgroundColor: ''}
    }
  }

  const getRiskLevelText = (risk) => {
    if (risk < 5) {
      return 'Low'
    } else if (risk >= 5 && risk <= 14) {
      return 'Medium'
    } else if (risk >= 15 && risk < 25) {
      return 'High'
    } else if (risk >= 25) {
      return 'Critical'
    } else {
      return ''
    }
  }

  return (
    <>
      <Drawer open={isOpen} onClose={onCancel} direction='right' className='' size='45%'>
        <div
          className=' modal-dialog-centered border-bottom border-gray-300'
          style={{
            position: 'sticky',
            top: 0,
            backgroundColor: 'white',
            padding: '14px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            paddingLeft: '15px',
            paddingRight: '12px',
          }}
        >
          <div className='fs-4 fw-bold mx-2' style={{margin: 0}}>
            {riskSource}
          </div>

          <div style={{display: 'flex', gap: '16px'}}>
            <div
              className='btn btn-icon btn-sm btn-active-light-primary '
              onClick={onCancel}
              aria-label='Close'
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='20'
                height='20'
                viewBox='0 0 24 24'
                fill='none'
                stroke='#5A5A5A'
                stroke-width='2'
                stroke-linecap='round'
                stroke-linejoin='round'
                className='icon icon-tabler icons-tabler-outline icon-tabler-x'
              >
                <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                <path d='M18 6l-12 12' />
                <path d='M6 6l12 12' />
              </svg>
            </div>
          </div>
        </div>
        <div
          className=' scroll-y overflow-auto p-6 mb-4'
          tabIndex={-1}
          style={{height: 'calc(100vh - 120px)'}}
        >
          <form id='kt_risk_form' className='form' onSubmit={handleSubmit}>
            <div className='modal-content'>
              <div className='modal-body'>
                <AddRiskControls id={id} isOpen={showModal} onClose={handleCloseModal} />

                <div className=''>
                  <div className='card mb-4 p-4' style={{background: '#F6F6F6'}}>
                    {risk_status === 'Incomplete' ? (
                      <>
                        <span
                          className='badge rounded-pill p-1 mt-2'
                          style={{border: '1px solid #C5C5C5', width: '110px'}}
                        >
                          <span className='mx-2'>
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              width='18'
                              height='18'
                              viewBox='0 0 24 24'
                              fill='none'
                              stroke='#EE3043'
                              strokeWidth='2'
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              className='icon icon-tabler icons-tabler-outline icon-tabler-xbox-x '
                            >
                              <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                              <path d='M12 21a9 9 0 0 0 9 -9a9 9 0 0 0 -9 -9a9 9 0 0 0 -9 9a9 9 0 0 0 9 9z' />
                              <path d='M9 8l6 8' />
                              <path d='M15 8l-6 8' />
                            </svg>
                          </span>
                          <span className='fs-7'>Incomplete</span>
                        </span>
                        <div className='fs-6 fw-semibold my-4'>
                          This risk is incomplete. Please fill all the mandatory fields below so
                          that the risk owner can approve.
                        </div>
                      </>
                    ) : risk_status === 'Needs approval' ? (
                      <>
                        <span
                          className='badge rounded-pill p-1 mt-2'
                          style={{border: '1px solid #C5C5C5', width: '140px'}}
                        >
                          <span className='mx-2'>
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              width='18'
                              height='18'
                              viewBox='0 0 24 24'
                              fill='none'
                              stroke='#FFA800'
                              strokeWidth='2'
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              className='icon icon-tabler icons-tabler-outline icon-tabler-file-check'
                            >
                              <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                              <path d='M14 3v4a1 1 0 0 0 1 1h4' />
                              <path d='M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z' />
                              <path d='M9 15l2 2l4 -4' />
                            </svg>
                          </span>
                          <span className='fs-7'>Needs Approval</span>
                        </span>
                        <div className='fs-6 fw-semibold my-4'>
                          Owner approval is pending for this risk scenario. <strong>Note:</strong>{' '}
                          Please assign owner if not assigned yet.
                        </div>
                        {currentUser?.userName?.toLowerCase().trim() ===
                          owner.toLowerCase().trim() &&
                          (loading ? (
                            <button
                              type='button'
                              className='btn btn-sm fs-6'
                              style={{
                                height: '40px',
                                width: '150px',
                                background: '#0E9F6E',
                                color: '#fff',
                              }}
                              disabled
                            >
                              Updating...
                              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                            </button>
                          ) : (
                            <button
                              type='button'
                              onClick={handleApprove}
                              className='btn btn-sm fs-6'
                              style={{
                                height: '40px',
                                width: '100px',
                                background: '#0E9F6E',
                                color: '#fff',
                              }}
                              onMouseEnter={(e: React.MouseEvent<HTMLElement>) =>
                                (e.currentTarget.style.backgroundColor = '#148363')
                              }
                              onMouseLeave={(e: React.MouseEvent<HTMLElement>) =>
                                (e.currentTarget.style.backgroundColor = '#0E9F6E')
                              }
                              disabled={currentUser?.role === 'Auditor' || !!currentAuditContext}
                            >
                              Approve
                            </button>
                          ))}
                      </>
                    ) : (
                      risk_status === 'Approved' && (
                        <>
                          <span
                            className='badge rounded-pill p-1 mt-2'
                            style={{border: '1px solid #C5C5C5', width: '110px'}}
                          >
                            <span className='mx-2'>
                              <svg
                                xmlns='http://www.w3.org/2000/svg'
                                width='20'
                                height='20'
                                viewBox='0 0 24 24'
                                fill='none'
                                stroke='#0E9F6E'
                                stroke-width='2'
                                stroke-linecap='round'
                                stroke-linejoin='round'
                                className='icon icon-tabler icons-tabler-outline icon-tabler-circle-check'
                              >
                                <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                <path d='M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0' />
                                <path d='M9 12l2 2l4 -4' />
                              </svg>
                            </span>
                            <span className='fs-7'>Approved</span>
                          </span>
                          <div className='fs-6 fw-semibold my-4'>
                            This risk scenario has been approved by the owner.
                          </div>
                        </>
                      )
                    )}
                  </div>
                  <div className='form-group mb-4'>
                    <label className='fw-semibold fs-6  required'>Risk name</label>
                    <input
                      type='text'
                      className={`form-control ${formErrors.riskSource ? 'is-invalid' : ''}`}
                      value={riskSource}
                      placeholder='Enter Risk Source'
                      onChange={(e) => {
                        setRiskSource(e.target.value)
                        setFormErrors((prev) => ({...prev, riskSource: ''}))
                      }}
                      disabled={currentUser?.role === 'Auditor' || !!currentAuditContext}
                    />
                    {formErrors.riskSource && (
                      <span className='text-danger'>{formErrors.riskSource}</span>
                    )}
                  </div>
                  <div className='form-group mb-4'>
                    <label className=' fw-semibold fs-6  required'>Risk Description</label>
                    <textarea
                      className={`form-control ${formErrors.description ? 'is-invalid' : ''}`}
                      placeholder='Enter risk description'
                      value={description}
                      id='discription'
                      rows={3}
                      onChange={(e) => {
                        setDescription(e.target.value)
                        setFormErrors((prev) => ({...prev, description: ''}))
                      }}
                      disabled={currentUser?.role === 'Auditor' || !!currentAuditContext}
                    ></textarea>
                    {formErrors.description && (
                      <span className='text-danger'>{formErrors.description}</span>
                    )}
                  </div>

                  <div className='form-group mb-4'>
                    <label className='fw-semibold my-2 fs-6 required'>Category</label>

                    <select
                      name='category'
                      className={`form-select ${formErrors.category ? 'is-invalid' : ''}`}
                      data-control='select2'
                      data-placeholder='Select Category'
                      onChange={(e) => {
                        const selectedCategories = Array.from(e.target.selectedOptions).map(
                          (option) => option.value
                        )
                        setCategory(selectedCategories)
                        setFormErrors((prev) => ({...prev, category: ''}))
                      }}
                      value={category}
                      disabled={currentUser?.role === 'Auditor' || !!currentAuditContext}
                    >
                      <option selected>Select category</option>

                      <option value='Access control'>Access control</option>
                      <option value='Artificial intelligence'>Artificial intelligence</option>
                      <option value='Asset management'>Asset management</option>
                      <option value='Business continuity and disaster recovery'>
                        Business continuity and disaster recovery
                      </option>
                      <option value='Communications security'>Communications security</option>
                      <option value='Compliance'>Compliance</option>
                      <option value='Cryptography'>Cryptography</option>
                      <option value='Environmental, social, and governance'>
                        Environmental, social, and governance
                      </option>
                      <option value='Fraud'>Fraud</option>
                      <option value='People operations'>People operations</option>
                      <option value='Incident response manegement'>
                        Incident response management
                      </option>
                      <option value='Information security policies'>
                        Information security policies
                      </option>
                      <option value='Operations security'>Operations security</option>
                      <option value='Information security operations'>
                        Information security operations
                      </option>
                      <option value='Physical and environmental security'>
                        Physical and environmental security
                      </option>
                      <option value='Privacy'>Privacy</option>
                      <option value='Vendor relationships'>Vendor relationships</option>
                      <option value='Software development and acquisition'>
                        Software development and acquisition
                      </option>
                      <option value='Trustworthiness'>Trustworthiness</option>
                    </select>
                    {formErrors.category && (
                      <span className='text-danger'>{formErrors.category}</span>
                    )}
                  </div>

                  <div className='form-group mb-4'>
                    <label className='fw-semibold my-2 fs-6 required'>Risk Owner</label>

                    <select
                      name='riskOwner'
                      className={`form-select ${formErrors.owner ? 'is-invalid' : ''}`}
                      data-control='select2'
                      data-placeholder='Select Owner'
                      onChange={(e) => {
                        setRiskOwner(e.target.value)
                        setFormErrors((prev) => ({...prev, owner: ''}))
                      }}
                      value={owner || ''}
                      disabled={currentUser?.role === 'Auditor' || !!currentAuditContext}
                    >
                      <option selected value=''>
                        Select owner
                      </option>
                      {owners
                        .filter((user) => user.role === 'Admin' || user.role === 'Owner')
                        .map((owner) => (
                          <option key={owner.id} value={owner.full_name}>
                            {owner.full_name}
                          </option>
                        ))}
                    </select>
                    {formErrors.owner && <span className='text-danger'>{formErrors.owner}</span>}
                  </div>
                  <div className='separator my-8'></div>

                  <div className='form-group mb-4'>
                    <h4 className='fw-bold required'>Risk Score</h4>
                    <span className='fs-6'>
                      Evaluate the risk based on event likelihood and its potential impact on your
                      organization with risk scores.
                    </span>
                  </div>
                  <div className='card p-4 mb-8' style={{background: '#F6F6F6'}}>
                    <div className='form-group mb-4'>
                      <div className='d-flex justify-content-between'>
                        <label className='fw-semibold fs-6'>
                          {' '}
                          <span className=' required'>Inherent Risk</span>
                          <OverlayTrigger
                            placement='right'
                            overlay={
                              <Tooltip id='tooltip-top'>
                                <span>
                                  Inherent risks are vulnerabilities or potential threats that exist
                                  within an organization before any controls or mitigation measures
                                  are implemented.
                                </span>
                              </Tooltip>
                            }
                          >
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              width='16'
                              height='16'
                              viewBox='0 0 24 24'
                              fill='none'
                              stroke='#5A5A5A'
                              stroke-width='2'
                              stroke-linecap='round'
                              stroke-linejoin='round'
                              className='icon icon-tabler icons-tabler-outline icon-tabler-info-circle mx-2'
                            >
                              <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                              <path d='M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0' />
                              <path d='M12 9h.01' />
                              <path d='M11 12h1v4h1' />
                            </svg>
                          </OverlayTrigger>
                        </label>{' '}
                        <span
                          className='badge p-2 rounded-pill'
                          style={{border: '1px solid #C5C5C5'}}
                        >
                          <span
                            className={`badge border rounded-circle mx-1 `}
                            style={{
                              width: '12px',
                              height: '12px',
                              padding: '2px',

                              ...getBadgeClass(
                                Number(inherent_likelihood) * Number(inherent_impact)
                              ),
                            }}
                          >
                            {' '}
                          </span>
                          <span className='fw-semibold  fs-7 mx-1'>
                            {getRiskLevelText(
                              Number(inherent_likelihood) * Number(inherent_impact)
                            )}
                          </span>
                        </span>
                      </div>
                    </div>
                    <RiskSlider
                      value={Number(inherent_likelihood)}
                      onChange={(value) => setInherentLikelihood(value.toString())}
                      disabled={currentUser?.role === 'Auditor' || !!currentAuditContext}
                      label='Likelihood'
                      tooltipText='Likelihood estimates how probable it is that a risk event will occur. Rate the chance of this risk happening based on historical data, current conditions, and expert judgment.'
                    />

                    <RiskSlider
                      value={Number(inherent_impact)}
                      onChange={(value) => setInherentImpact(value.toString())}
                      disabled={currentUser?.role === 'Auditor' || !!currentAuditContext}
                      label='Impact'
                      tooltipText="Impact measures the severity of consequences if a risk event occurs. Evaluate how this risk could affect your organization's objectives, operations, finances, or reputation."
                    />
                  </div>

                  <div className='card p-4 ' style={{background: '#F6F6F6'}}>
                    <div className='form-group mb-4'>
                      <div className='d-flex justify-content-between'>
                        <label className='fw-semibold fs-6'>
                          {' '}
                          <span className=' required'>Residual Risk</span>
                          <OverlayTrigger
                            placement='right'
                            overlay={
                              <Tooltip id='tooltip-top'>
                                <span>
                                  Residual Risk represents the remaining risk after control measures
                                  have been implemented.
                                </span>
                              </Tooltip>
                            }
                          >
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              width='16'
                              height='16'
                              viewBox='0 0 24 24'
                              fill='none'
                              stroke='#5A5A5A'
                              stroke-width='2'
                              stroke-linecap='round'
                              stroke-linejoin='round'
                              className='icon icon-tabler icons-tabler-outline icon-tabler-info-circle mx-2'
                            >
                              <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                              <path d='M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0' />
                              <path d='M12 9h.01' />
                              <path d='M11 12h1v4h1' />
                            </svg>
                          </OverlayTrigger>
                        </label>{' '}
                        <span
                          className='badge p-2 rounded-pill'
                          style={{border: '1px solid #C5C5C5'}}
                        >
                          <span
                            className={`badge border rounded-circle mx-1 `}
                            style={{
                              width: '12px',
                              height: '12px',
                              padding: '2px',

                              ...getBadgeClass(
                                Number(residual_likelihood) * Number(residual_impact)
                              ),
                            }}
                          >
                            {' '}
                          </span>
                          <span className='fw-semibold  fs-7 mx-1'>
                            {getRiskLevelText(
                              Number(residual_likelihood) * Number(residual_impact)
                            )}
                          </span>
                        </span>
                      </div>
                    </div>
                    <RiskSlider
                      value={Number(residual_likelihood)}
                      onChange={(value) => setResidualLikelihood(value.toString())}
                      disabled={currentUser?.role === 'Auditor' || !!currentAuditContext}
                      label='Likelihood'
                      tooltipText='Estimate how probable the risk event is now, considering your current control measures. Has the chance of occurrence decreased?'
                    />
                    <RiskSlider
                      value={Number(residual_impact)}
                      onChange={(value) => setResidualImpact(value.toString())}
                      disabled={currentUser?.role === 'Auditor' || !!currentAuditContext}
                      label='Impact'
                      tooltipText='Evaluate the potential consequences if the risk occurs, taking into account your mitigation strategies. Has the severity of the impact been reduced?'
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className='separator my-8'></div>
            <div className='form-group mb-4'>
              <h4 className='fw-bold  required'>Treatment</h4>
              <span className='fs-6'>
                Select a treatment plan for this risk based on your risk source.
              </span>

              {['Mitigate', 'Avoid', 'Transfer', 'Accept'].map((value) => (
                <div key={value} className='d-flex justify-content-start mt-4 mb-2'>
                  <div className='form-check form-check-custom form-check-solid mx-2 p-1'>
                    <input
                      className={`form-check-input `}
                      type='radio'
                      value={value}
                      id={`treatment${value}`}
                      name='treatment'
                      checked={treatment === value}
                      onChange={handleChange}
                      disabled={currentUser?.role === 'Auditor' || !!currentAuditContext}
                      style={formErrors.treatment ? {border: '1px solid red'} : {}}
                    />
                  </div>
                  <span>
                    <span className='text-dark fw-semibold d-block fs-6'>{value}</span>
                    <span className='d-block fs-6' style={{color: '#5A5A5A'}}>
                      {value === 'Mitigate' &&
                        "Implement controls to reduce the risk's impact or likelihood"}
                      {value === 'Avoid' &&
                        'Stop activities that create risk for your organization'}
                      {value === 'Transfer' &&
                        'Shift responsibility for the risk to another party, such as an insurer'}
                      {value === 'Accept' && 'Keep the risk as is without taking additional action'}
                    </span>
                  </span>
                </div>
              ))}
              {formErrors.treatment && <span className='text-danger'>{formErrors.treatment}</span>}
            </div>

            <div className='separator my-8'></div>
            <div className='my-4 d-flex justify-content-between'>
              <div className='mb-4'>
                <div>
                  <h4 className='fw-bold'>
                    Map Controls{' '}
                    <span
                      className='fs-6'
                      style={{color: '#78829D', fontWeight: '500', paddingLeft: '5px'}}
                    >
                      (Optional)
                    </span>
                  </h4>
                  <div className='fs-6 mb-2'>
                    Select controls that may reduce the residual risk score.
                  </div>
                </div>
              </div>

              <Button
                type='button'
                btnType='primary'
                onClick={() =>
                  currentUser?.account_type === 'Trial' ? setShowModal2(true) : handleboard()
                }
                disabled={currentUser?.role === 'Auditor' || !!currentAuditContext}
              >
                Add Controls
              </Button>
            </div>

            <div className='card mb-4' style={{background: '#F6F6F6'}}>
              <div
                className='card-header fs-6 fw-bold p-7'
                style={{borderBottom: '1px solid #C5C5C5'}}
              >
                {' '}
                CONTROLS
              </div>

              <div className='card-body p-0 px-7'>
                <table className='table'>
                  <tbody>
                    {addedControls && addedControls.length > 0 ? (
                      addedControls.map((control) => (
                        <tr key={control.id} style={{borderBottom: '1px dotted #C5C5C5'}}>
                          <td
                            className='fs-6 fw-semibold py-8 text-hover-primary'
                            style={{cursor: 'pointer'}}
                            key={control.id}
                            onClick={() => handleRiskClick(control.id)}
                          >
                            {control.control_title}
                          </td>
                          <td className='fs-6 fw-semibold py-8'>
                            {control.control_framework_mappings}
                          </td>
                          <td className='fs-6 fw-semibold py-6'>
                            <button
                              className='btn btn-icon btn-sm btn-active-light-primary'
                              onClick={() => handleDeleteControl(control)}
                              style={{
                                transition: 'all 0.2s ease',
                              }}
                              onMouseEnter={(e) => {
                                if (e.currentTarget) {
                                  e.currentTarget.style.backgroundColor = '#F6F6F6'
                                  const svgElement = e.currentTarget.querySelector('svg')
                                  if (svgElement) {
                                    svgElement.style.stroke = '#0E9F6E'
                                  }
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (e.currentTarget) {
                                  e.currentTarget.style.backgroundColor = '#F6F6F6'
                                  const svgElement = e.currentTarget.querySelector('svg')
                                  if (svgElement) {
                                    svgElement.style.stroke = '#5A5A5A'
                                  }
                                }
                              }}
                              disabled={currentUser?.role === 'Auditor' || !!currentAuditContext}
                            >
                              <svg
                                xmlns='http://www.w3.org/2000/svg'
                                width='20'
                                height='20'
                                viewBox='0 0 24 24'
                                fill='none'
                                stroke='#5E6274'
                                strokeWidth='2'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                className='icon icon-tabler icons-tabler-outline icon-tabler-x '
                              >
                                <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                <path d='M18 6l-12 12' />
                                <path d='M6 6l12 12' />
                              </svg>
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <>
                        <div
                          className='card-body'
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            width='24'
                            height='24'
                            viewBox='0 0 24 24'
                            fill='none'
                            stroke='#5A5A5A'
                            strokeWidth='2'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            className='icon icon-tabler icons-tabler-outline icon-tabler-adjustments-horizontal'
                          >
                            <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                            <path d='M14 6m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0' />
                            <path d='M4 6l8 0' />
                            <path d='M16 6l4 0' />
                            <path d='M8 12m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0' />
                            <path d='M4 12l2 0' />
                            <path d='M10 12l10 0' />
                            <path d='M17 18m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0' />
                            <path d='M4 18l11 0' />
                            <path d='M19 18l1 0' />
                          </svg>
                          <br />
                          <span className='fs-6 fw-semibold'>No Controls added</span>
                        </div>
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </form>
        </div>
        {currentUser?.role !== 'Auditor' && (
          <div
            className='offcanvas-footer d-flex justify-content-end  border-top py-4 '
            style={{position: 'sticky', bottom: 0, backgroundColor: 'white'}}
          >
            <Button btnType='secondary' className='me-2' onClick={onCancel}>
              Cancel
            </Button>
            {isLoading ? (
              <Button btnType='primary' disabled>
                Saving...{' '}
                <span className='spinner-border spinner-border-sm align-middle m-2'></span>
              </Button>
            ) : (
              <Button
                btnType='primary'
                disabled={currentUser?.role === 'Auditor' || !!currentAuditContext}
                onClick={handleSubmit}
              >
                Save
              </Button>
            )}
          </div>
        )}
      </Drawer>
      {isDeleteModalOpen && (
        <ConfirmationModal
          isModalOpen={isDeleteModalOpen}
          hideModal={() => setIsDeleteModalOpen(false)}
          confirmBtnClickHandler={handleConfirmControlDelete}
          toDeleteItemTitle={toDeleteControl?.control_title}
          isLoading={isDeleting}
          title='Remove Control'
          message={`Are you sure you want to remove ${toDeleteControl?.control_title} control from risk scenario? This action cannot be undone.`}
          type='delete'
          confirmButtonText='Remove'
        />
      )}
      <UpgradePlan show={showModal2} onClose={() => setShowModal2(false)} />
    </>
  )
}

export {EditRiskModal}
